// Chromium's "ResizeObserver loop completed with undelivered notifications" is
// a per-frame layout warning, not a JS failure, and a tab stuck in one emits
// it ~50 times a second for hours (ENG-1898: 1M+ indexed log events on a bad
// day from one or two workstations). RUM already drops it in its own
// `beforeSend` (ENG-1298); this keeps the Logs SDK from shipping the raw
// stream while preserving the signal: the first hit of every window goes
// through as a `warn` carrying how many were seen since the last one, so
// onset time and rate survive and a spike still shows on a board.
const RESIZE_OBSERVER_LOOP_MESSAGE = "ResizeObserver loop";
const RESIZE_OBSERVER_LOOP_WINDOW_MS = 60_000;

type SampledLog = {
  message: string;
  status: string;
  error?: unknown;
  [key: string]: unknown;
};

type Options = {
  windowMs?: number;
  now?: () => number;
};

function isResizeObserverLoopLog(log: SampledLog): boolean {
  const errorMessage = (log.error as { message?: string } | undefined)?.message;
  return (
    log.message.includes(RESIZE_OBSERVER_LOOP_MESSAGE) ||
    Boolean(errorMessage?.includes(RESIZE_OBSERVER_LOOP_MESSAGE))
  );
}

/**
 * Returns a `beforeSend`-shaped predicate: `true` to forward the log (mutated
 * into the aggregated warn), `false` to drop it. Unrelated logs pass through
 * untouched.
 */
export function createResizeObserverLoopSampler({
  windowMs = RESIZE_OBSERVER_LOOP_WINDOW_MS,
  now = Date.now
}: Options = {}) {
  let windowStart: number | null = null;
  let seen = 0;

  return (log: SampledLog): boolean => {
    if (!isResizeObserverLoopLog(log)) return true;

    seen += 1;
    const at = now();
    if (windowStart !== null && at - windowStart < windowMs) return false;

    windowStart = at;
    log.status = "warn";
    log.resize_observer_loop = { count: seen, window_ms: windowMs };
    delete log.error;
    seen = 0;
    return true;
  };
}
